/**
 * One-time cleanup of the guest ad-hoc view snapshots.
 *
 * `guestId` (the 24-char ObjectId) was part of DEFAULT_GUEST_FIELDS between
 * 7a421dac (2026-07-12) and its removal. Whenever a DEFAULT view was applied,
 * ViewsProvider captured the visible columns into `view-guest-{eventId}-adhoc`
 * — so the column outlives the default it came from, in a view the user never
 * chose it for.
 *
 * This prunes those snapshots exactly once. It is deliberately NOT a filter on
 * the read path: `guestId` stays available in the column picker, and a
 * deliberate pick has to survive the next reload.
 *
 * Named views are server-side and untouched; removing a column there is an
 * explicit edit the user makes and saves.
 */
import { getPref, setPref } from "@/lib/storage/userPrefs";

export const ADHOC_GUESTID_PRUNE_KEY = "view-guest-adhoc-guestid-pruned";

/** `view-guest-{eventId}-adhoc` — guest tables only. */
const ADHOC_KEY = /^view-guest-.+-adhoc$/;

export function pruneAdhocGuestIdOnce(): void {
  if (typeof window === "undefined") return;
  if (getPref(ADHOC_GUESTID_PRUNE_KEY) === "1") return;

  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && ADHOC_KEY.test(key)) keys.push(key);
    }

    for (const key of keys) {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      try {
        const view = JSON.parse(raw) as { fields?: string[] };
        if (!Array.isArray(view.fields)) continue;
        if (!view.fields.includes("guestId")) continue;
        view.fields = view.fields.filter((f) => f !== "guestId");
        window.localStorage.setItem(key, JSON.stringify(view));
      } catch {
        // A snapshot we cannot parse is left exactly as it was — getJSONPref
        // falls back to the default view for it anyway.
      }
    }

    setPref(ADHOC_GUESTID_PRUNE_KEY, "1");
  } catch {
    // localStorage unavailable (private mode). Without the marker this simply
    // retries on the next load, which is harmless.
  }
}
